/**
 * @fileoverview Sample-data resolution for precedent previews (MOR-923)
 *
 * Builds a resolver that turns a field key into a plausible sample value so
 * the precedent library can preview letters and forms "as generated"
 * instead of as field chips. Resolution order:
 *
 *  1. The template's own AI-extracted `exampleValue` (by key or canonical
 *     key) — the most template-specific sample we have.
 *  2. The canonical field catalogue's `placeholder` (e.g. practitioner
 *     "Morgan Liquidsworth", firm "Morgan & Associates Pty Ltd").
 *  3. A field-type default (dates, currency, numbers, emails, phones).
 *  4. The field's label in brackets, so unmatched fields stay visible.
 */

import {
  applyFieldModifiers,
  applyStackLayout,
  parseModifiers,
} from "@/ui/composed/editor/field-modifiers";

import { CANONICAL_FIELDS_MAP } from "../../../convex/settings/precedents/canonicalFields";

export interface SampleTemplateField {
  key: string;
  canonicalKey?: string;
  exampleValue?: string;
  fieldType?: string;
  label?: string;
}

const FIELD_TYPE_SAMPLES: Record<string, string> = {
  date: "17/08/2026",
  currency: "$12,345.67",
  number: "42",
  email: "contact@example.com.au",
  phone: "(02) 0000 0000",
  abn: "00 000 000 000",
  acn: "000 000 000",
  address: "Level 42, 1 Liquidation Lane, Solvencyville NSW 2000",
  signature: "Morgan Liquidsworth",
};

/**
 * Sample per bindable contact part, shared by the fan-out `contact_*` and
 * reference `firmref_*` families (both use the same part vocabulary). Without
 * these, contact-bound placements fell through to a bracketed label because
 * they are not canonical catalogue keys.
 */
const CONTACT_PART_SAMPLES: Record<string, string> = {
  name: "Jordan Example",
  first_name: "Jordan",
  middle_name: "Alex",
  last_name: "Example",
  full_name: "Jordan Alex Example",
  date_of_birth: "14/03/1978",
  email: "jordan@examplecreditor.com.au",
  phone: "(02) 9000 1234",
  address_full: "Unit 5, 22 Sample Street, Solvencyville NSW 2000",
  address_line1: "22 Sample Street",
  address_line2: "Unit 5",
  address_suburb: "Solvencyville",
  address_state: "NSW",
  address_postcode: "2000",
  abn: "12 345 678 901",
  acn: "123 456 789",
};

/**
 * Last-resort sample derived from the key/label wording, so an unmatched
 * field still previews as something that looks like real data rather than a
 * bracketed placeholder. Ordered most-specific first.
 */
function heuristicSample(key: string, label?: string): string | null {
  const hay = `${key} ${label ?? ""}`.toLowerCase();
  const has = (...needles: string[]) => needles.some((n) => hay.includes(n));

  if (has("signature", "signed by")) return FIELD_TYPE_SAMPLES.signature;
  if (has("email")) return FIELD_TYPE_SAMPLES.email;
  if (has("phone", "mobile", "telephone", "fax")) return "(02) 9000 1234";
  if (has("abn")) return FIELD_TYPE_SAMPLES.abn;
  if (has("acn")) return FIELD_TYPE_SAMPLES.acn;
  if (has("postcode")) return "2000";
  if (has("state")) return "NSW";
  if (has("suburb", "city", "town")) return "Solvencyville";
  if (has("address")) return FIELD_TYPE_SAMPLES.address;
  if (has("date", "_dob", "birth")) return FIELD_TYPE_SAMPLES.date;
  if (has("rate", "amount", "fee", "cost", "price", "balance", "total", "$"))
    return "$450.00";
  if (has("percent", "%")) return "10%";
  if (has("qty", "quantity", "count", "number of")) return "3";
  if (has("website", "url", "domain")) return "www.example.com.au";
  if (has("name")) return "Jordan Example";
  if (has("reference", "ref no", "file no", "matter")) return "REF-10432";
  return null;
}

/** Strip a leading `contact_<domain>_` / `firmref_<category>_` prefix. */
function contactPartFromKey(key: string): string | null {
  for (const part of Object.keys(CONTACT_PART_SAMPLES)) {
    if (key.endsWith(`_${part}`)) return part;
  }
  return null;
}

export type SampleValueResolver = (key: string, label?: string) => string;

/**
 * Build a `(key, label?) => sample` resolver from the template's extracted
 * fields plus the org's custom field definitions.
 */
export function buildSampleValueResolver(opts: {
  templateFields?: ReadonlyArray<SampleTemplateField>;
  customFields?: ReadonlyArray<{
    key: string;
    label: string;
    fieldType?: string;
  }>;
}): SampleValueResolver {
  const byKey = new Map<string, SampleTemplateField>();
  for (const f of opts.templateFields ?? []) {
    if (!byKey.has(f.key)) byKey.set(f.key, f);
    if (f.canonicalKey && !byKey.has(f.canonicalKey)) {
      byKey.set(f.canonicalKey, f);
    }
  }
  const customByKey = new Map((opts.customFields ?? []).map((f) => [f.key, f]));

  return (key, label) => {
    const templateField = byKey.get(key);
    const example = templateField?.exampleValue?.trim();
    if (example) return example;

    const canonical = CANONICAL_FIELDS_MAP[key];
    if (canonical?.placeholder) return canonical.placeholder;

    // Contact-bound placements (`contact_<domain>_<part>`) and firm-contact
    // references (`firmref_<category>_<part>`) share one part vocabulary and
    // are never in the canonical catalogue, so resolve them by part.
    if (key.startsWith("contact_") || key.startsWith("firmref_")) {
      const part = contactPartFromKey(key);
      if (part) return CONTACT_PART_SAMPLES[part];
    }

    const fieldType =
      templateField?.fieldType ??
      canonical?.fieldType ??
      customByKey.get(key)?.fieldType;
    if (fieldType && FIELD_TYPE_SAMPLES[fieldType]) {
      return FIELD_TYPE_SAMPLES[fieldType];
    }

    const resolvedLabel =
      label ??
      templateField?.label ??
      canonical?.label ??
      customByKey.get(key)?.label;

    // Wording-based guess before giving up — a plausible-looking value is far
    // more useful in a preview than "[Some Label]".
    const guess = heuristicSample(key, resolvedLabel);
    if (guess) return guess;

    return `[${resolvedLabel ?? key}]`;
  };
}

/**
 * Escape a resolved sample value for the markup path, matching the escaping
 * the generated-document path applies before `applyStackLayout`.
 */
function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Replace every field chip (`<span data-field-key="…">`) in a rendered
 * preview HTML string with its resolved sample value as plain text, so the
 * document reads exactly like a generated letter. Non-chip markup is left
 * untouched. Runs in the browser (uses DOMParser).
 *
 * Field-modifier chips carry their modifiers on `data-field-mods`; we apply
 * them (case/wrap/plural/possessive/date-format/prefix) to the resolved
 * sample value with the same `applyFieldModifiers` used at generation time, so
 * the sample preview matches the real letter — e.g. an `abn_label` prefix
 * renders "ABN 00 000 000 000" and a `lower` case modifier lowercases the
 * value.
 *
 * `stack` is the one modifier that is not purely textual: it renders a
 * `\n`-separated value one entry per line, which only survives as markup. It
 * therefore takes the same `applyStackLayout` path the generated document
 * uses, and the resulting nodes replace the chip — a text node would collapse
 * the newline to a space.
 */
export function substituteSampleValuesInHtml(
  html: string,
  resolve: SampleValueResolver,
): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  for (const chip of Array.from(doc.querySelectorAll("[data-field-key]"))) {
    const key = chip.getAttribute("data-field-key") ?? "";
    const label =
      chip.getAttribute("data-field-label") ??
      chip.textContent?.trim() ??
      undefined;
    const resolved = resolve(key, label ?? undefined);
    const mods = parseModifiers(chip.getAttribute("data-field-mods"));
    const modified = applyFieldModifiers(resolved, mods);
    if (mods.includes("stack")) {
      const holder = doc.createElement("div");
      holder.innerHTML = applyStackLayout(escapeHtmlText(modified), key);
      chip.replaceWith(...Array.from(holder.childNodes));
      continue;
    }
    chip.replaceWith(doc.createTextNode(modified));
  }
  return doc.body.innerHTML;
}
